import React from "react";
import { Autocomplete, MenuItem, TextField } from "@mui/material";
import InputField from "components/Input/InputField";
import TextArea from "components/Input/TextArea";

const ClientDetailsFormFields = ({ fields = [], values = {}, onChange, disabled = false }) => {
  const handleSimpleChange = (id) => (e) => onChange?.(id, e.target.value);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {fields.map((field) => {
        if (!field?.id) return null;
        const wrapperClass = field.layout === "full" ? "md:col-span-2" : "";
        const value = values[field.id] ?? "";

        if (field.type === "textarea") {
          return (
            <div key={field.id} className={wrapperClass}>
              <TextArea
                label={field.label}
                name={field.id}
                value={value}
                onChange={handleSimpleChange(field.id)}
                rows={3}
                required={field.required}
                disabled={disabled}
              />
            </div>
          );
        }

        if (field.type === "select") {
          const options = Array.isArray(field.options) ? field.options : [];
          return (
            <div key={field.id} className={wrapperClass}>
              <TextField
                select
                fullWidth
                size="small"
                label={field.label}
                value={value}
                onChange={handleSimpleChange(field.id)}
                required={field.required}
                disabled={disabled}
              >
                {options.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          );
        }

        if (field.type === "autocomplete") {
          const options = Array.isArray(field.options) ? field.options : [];
          return (
            <div key={field.id} className={wrapperClass}>
              <Autocomplete
                freeSolo
                size="small"
                options={options}
                value={value}
                disabled={disabled}
                onChange={(_, v) => onChange?.(field.id, v || "")}
                onInputChange={(_, v) => onChange?.(field.id, v || "")}
                renderInput={(params) => (
                  <TextField {...params} label={field.label} required={field.required} />
                )}
              />
            </div>
          );
        }

        if (field.type === "checkbox-group") {
          const options = Array.isArray(field.options) ? field.options : [];
          const selected = Array.isArray(value) ? value : [];
          const toggle = (opt) => {
            if (disabled) return;
            const next = selected.includes(opt)
              ? selected.filter((s) => s !== opt)
              : [...selected, opt];
            onChange?.(field.id, next);
          };
          return (
            <div key={field.id} className={wrapperClass}>
              <label className="block uppercase text-app-text-secondary text-xs font-semibold mb-2">
                {field.label} {field.required && <sup className="text-red-500">*</sup>}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {options.length === 0 && (
                  <span className="text-xs text-app-text-muted italic">No options configured</span>
                )}
                {options.map((opt) => (
                  <label
                    key={opt}
                    className={`flex items-center gap-2 ${
                      disabled ? "opacity-60" : "cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(opt)}
                      onChange={() => toggle(opt)}
                      disabled={disabled}
                      className="text-primary rounded"
                    />
                    <span className="text-sm text-app-text">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        }

        return (
          <div key={field.id} className={wrapperClass}>
            <InputField
              label={field.label}
              name={field.id}
              value={value}
              onChange={handleSimpleChange(field.id)}
              required={field.required}
              type={field.type}
              disabled={disabled}
            />
          </div>
        );
      })}
    </div>
  );
};

export default ClientDetailsFormFields;
